<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth.js'
import { useAuditStore } from '../stores/auditStore.js'
import AdminActionDialog from '../components/AdminActionDialog.vue'
import {
  formatAuditActor,
  formatAuditMetadata,
  formatAuditTimestamp,
  getAuditActionMeta,
  getAuditOutcomeMeta,
} from '../utils/auditUi.js'
import { toAdminLoadingMessage } from '../utils/adminUi.js'
import { useDebouncedValue } from '../utils/debounce.js'
import { DEFAULT_ADMIN_SEARCH_DEBOUNCE_MS, isDataStale, toPositiveInt } from '../utils/operationsUi.js'
import { downloadCsv, getRecentExportHistory, recordExportEvent, toCsv, toLifecycleFilename } from '../utils/exportUi.js'
import { useQueryState } from '../composables/useQueryState.js'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const auditStore = useAuditStore()
const {
  logs,
  loading,
  error,
  page,
  pageSize,
  total,
  filters,
  lastSuccessfulFetchAt,
  degradedWarning,
  partialDataWarning,
} = storeToRefs(auditStore)
const expandedLogId = ref(null)
const searchInput = ref('')
const debouncedSearchInput = useDebouncedValue(searchInput, DEFAULT_ADMIN_SEARCH_DEBOUNCE_MS)
const exportDialogOpen = ref(false)
const exportLoading = ref(false)
const exportError = ref('')
const exportHistory = ref([])

const filterModel = reactive({
  action: '',
  entity_type: '',
  organization_id: '',
})

const pageSizeOptions = [25, 50, 100]

const knownActionOptions = [
  { value: '', label: 'All actions' },
  { value: 'ORG_SOFT_DELETE', label: 'Organization Deleted' },
  { value: 'ORG_RESTORE', label: 'Organization Restored' },
  { value: 'SUBORG_SOFT_DELETE', label: 'Suborganization Deleted' },
  { value: 'SUBORG_RESTORE', label: 'Suborganization Restored' },
  { value: 'LOGIN_DENIED_ORG_DELETED', label: 'Login Blocked' },
  { value: 'ACCESS_REVOKED_ORG_DELETED', label: 'Access Revoked' },
]

const entityTypeOptions = [
  { value: '', label: 'All entity types' },
  { value: 'organization', label: 'Organization' },
  { value: 'suborganization', label: 'Suborganization' },
  { value: 'user', label: 'User' },
  { value: 'auth', label: 'Auth' },
]

const totalPages = computed(() => {
  const normalizedTotal = Number(total.value || 0)
  const normalizedPageSize = Number(pageSize.value || 0)
  if (!normalizedTotal || !normalizedPageSize) return 1
  return Math.max(1, Math.ceil(normalizedTotal / normalizedPageSize))
})

const hasPreviousPage = computed(() => Number(page.value) > 1)
const hasNextPage = computed(() => Number(page.value) < Number(totalPages.value))
const staleData = computed(() => isDataStale(lastSuccessfulFetchAt.value, 120000))

const queryState = useQueryState({
  route,
  router,
  entries: [
    {
      queryKey: 'page',
      type: 'int',
      defaultValue: 1,
      get: () => page.value,
      set: (value) => { page.value = toPositiveInt(value, page.value) },
    },
    {
      queryKey: 'page_size',
      type: 'int',
      defaultValue: 50,
      get: () => pageSize.value,
      set: (value) => { pageSize.value = toPositiveInt(value, pageSize.value) },
    },
    {
      queryKey: 'action',
      defaultValue: '',
      get: () => filterModel.action,
      set: (value) => { filterModel.action = String(value || '') },
    },
    {
      queryKey: 'entity_type',
      defaultValue: '',
      get: () => filterModel.entity_type,
      set: (value) => { filterModel.entity_type = String(value || '') },
    },
    {
      queryKey: 'organization_id',
      defaultValue: '',
      get: () => filterModel.organization_id,
      set: (value) => { filterModel.organization_id = String(value || '') },
    },
    {
      queryKey: 'search',
      defaultValue: '',
      get: () => searchInput.value,
      set: (value) => { searchInput.value = String(value || '') },
    },
  ],
})

const syncQueryFromState = async () => {
  await queryState.syncToRoute()
}

const hydrateStateFromQuery = () => {
  queryState.hydrateFromQuery()
  filterModel.action = filterModel.action || String(filters.value.action || '')
  filterModel.entity_type = filterModel.entity_type || String(filters.value.entity_type || '')
  filterModel.organization_id = filterModel.organization_id || String(filters.value.organization_id || '')
  searchInput.value = searchInput.value || String(filters.value.search || '')
}

const getLogKey = (log, index = 0) => {
  if (log?.id !== null && log?.id !== undefined && log?.id !== '') return String(log.id)
  return `${log?.created_at || log?.timestamp || 'log'}:${log?.action || 'unknown'}:${log?.entity_id || index}`
}

const formatDetailValue = (value, placeholder = '—') => {
  if (value === null || value === undefined) return placeholder
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed ? trimmed : placeholder
  }
  return String(value)
}

const formatActorUsername = (log) => {
  return formatDetailValue(log?.actor_username, 'System')
}

const resolveActor = (log) => formatAuditActor(log)

const resolveEntityName = (log) => {
  return log?.entity_name || log?.entity_display_name || log?.entity_id || '-'
}

const formatActionDetail = (action) => {
  const normalizedAction = String(action || '').trim()
  if (!normalizedAction) return '—'

  const actionMeta = getAuditActionMeta(normalizedAction)
  return actionMeta.label === 'Unknown Action' || actionMeta.label === normalizedAction
    ? normalizedAction
    : `${actionMeta.label} (${normalizedAction})`
}

const getMetadataEntries = (log) => formatAuditMetadata(log?.metadata_json)

const getAuditDetailFields = (log) => {
  const outcomeMeta = getAuditOutcomeMeta(log?.success)

  return [
    { label: 'Exact timestamp', value: formatAuditTimestamp(log?.created_at || log?.timestamp) },
    { label: 'Actor username', value: formatActorUsername(log) },
    { label: 'Actor user ID', value: formatDetailValue(log?.actor_user_id) },
    { label: 'Action', value: formatActionDetail(log?.action) },
    { label: 'Entity type', value: formatDetailValue(log?.entity_type) },
    { label: 'Entity ID', value: formatDetailValue(log?.entity_id) },
    { label: 'Entity name', value: formatDetailValue(log?.entity_name || log?.entity_display_name) },
    { label: 'Organization ID', value: formatDetailValue(log?.organization_id) },
    { label: 'Suborganization ID', value: formatDetailValue(log?.suborganization_id) },
    { label: 'Outcome', value: outcomeMeta.label },
    { label: 'Reason', value: formatDetailValue(log?.reason, 'None') },
    { label: 'IP address', value: formatDetailValue(log?.ip_address, 'Not available') },
  ]
}

const isExpanded = (log, index = 0) => expandedLogId.value === getLogKey(log, index)

const toggleExpandedLog = (log, index = 0) => {
  const logKey = getLogKey(log, index)
  expandedLogId.value = expandedLogId.value === logKey ? null : logKey
}

const applyFilters = async () => {
  expandedLogId.value = null
  await auditStore.setFilters({
    action: String(filterModel.action || ''),
    entity_type: String(filterModel.entity_type || ''),
    organization_id: String(filterModel.organization_id || ''),
    search: String(searchInput.value || ''),
  })
  await syncQueryFromState()
}

const clearFilters = async () => {
  expandedLogId.value = null
  filterModel.action = ''
  filterModel.entity_type = ''
  filterModel.organization_id = ''
  searchInput.value = ''
  await auditStore.clearFilters()
  await syncQueryFromState()
}

const refresh = async () => {
  expandedLogId.value = null
  await auditStore.fetchAuditLogs()
  exportHistory.value = getRecentExportHistory()
}

const goToPreviousPage = async () => {
  if (!hasPreviousPage.value) return
  expandedLogId.value = null
  await auditStore.setPage(Number(page.value) - 1)
  await syncQueryFromState()
}

const goToNextPage = async () => {
  if (!hasNextPage.value) return
  expandedLogId.value = null
  await auditStore.setPage(Number(page.value) + 1)
  await syncQueryFromState()
}

const updatePageSize = async (nextPageSize) => {
  expandedLogId.value = null
  await auditStore.setPageSize(Number(nextPageSize))
  await syncQueryFromState()
}

const openExportDialog = () => {
  exportError.value = ''
  exportDialogOpen.value = true
}

const closeExportDialog = () => {
  if (exportLoading.value) return
  exportDialogOpen.value = false
}

const exportAuditCsv = async () => {
  if (!logs.value.length || exportLoading.value) return
  exportLoading.value = true
  exportError.value = ''
  try {
    const rows = logs.value.map((entry) => ({
      timestamp: formatAuditTimestamp(entry?.created_at || entry?.timestamp),
      actor: formatAuditActor(entry),
      action: entry?.action || '',
      entity_type: entry?.entity_type || '',
      entity_id: entry?.entity_id || '',
      outcome: getAuditOutcomeMeta(entry?.success).label,
      reason: entry?.reason || '',
    }))
    const csv = toCsv(rows, [
      { key: 'timestamp', label: 'timestamp' },
      { key: 'actor', label: 'actor' },
      { key: 'action', label: 'action' },
      { key: 'entity_type', label: 'entity_type' },
      { key: 'entity_id', label: 'entity_id' },
      { key: 'outcome', label: 'outcome' },
      { key: 'reason', label: 'reason' },
    ])
    const filename = toLifecycleFilename({
      scope: 'audit-logs',
      lifecycle: filterModel.action || 'all-actions',
    })
    downloadCsv(filename, csv)
    recordExportEvent({
      exported_by: authStore.user?.username || authStore.user?.email || 'unknown',
      export_scope: 'audit_logs',
      partial_export_warning: Number(total.value) > Number(logs.value.length),
      record_count: rows.length,
    })
    exportHistory.value = getRecentExportHistory()
    exportDialogOpen.value = false
  } catch (err) {
    exportError.value = 'Failed to export audit log report.'
  } finally {
    exportLoading.value = false
  }
}

onMounted(async () => {
  hydrateStateFromQuery()
  await auditStore.fetchAuditLogs()
  await syncQueryFromState()
  exportHistory.value = getRecentExportHistory()
})

watch(debouncedSearchInput, async (nextValue, previousValue) => {
  if (nextValue === previousValue) return
  if (String(nextValue || '') === String(filters.value.search || '')) return
  await applyFilters()
})

watch(logs, (nextLogs) => {
  if (!expandedLogId.value) return

  const hasExpandedLog = Array.isArray(nextLogs)
    && nextLogs.some((log, index) => getLogKey(log, index) === expandedLogId.value)

  if (!hasExpandedLog) {
    expandedLogId.value = null
  }
})
</script>

<template>
  <section class="audit-log-view">
    <header class="audit-header">
      <div>
        <h1>Audit Timeline</h1>
        <p class="muted">Read-only system activity feed for superadmins.</p>
        <p v-if="lastSuccessfulFetchAt" class="muted">Last successful refresh: {{ formatAuditTimestamp(lastSuccessfulFetchAt) }}</p>
      </div>
      <div class="filter-actions">
        <button class="btn" type="button" :disabled="loading" @click="refresh">
          {{ loading ? 'Refreshing...' : 'Refresh' }}
        </button>
        <button class="btn btn-secondary" type="button" :disabled="loading || !logs.length" @click="openExportDialog">
          Export CSV
        </button>
      </div>
    </header>

    <form class="filter-row" @submit.prevent="applyFilters">
      <label>
        Action
        <select v-model="filterModel.action" :disabled="loading">
          <option v-for="option in knownActionOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </label>

      <label>
        Entity Type
        <select v-model="filterModel.entity_type" :disabled="loading">
          <option v-for="option in entityTypeOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </label>

      <label>
        Organization ID
        <input v-model="filterModel.organization_id" type="text" placeholder="Optional" :disabled="loading" />
      </label>

      <label>
        Search
        <input v-model="searchInput" type="search" placeholder="Actor, reason, entity..." :disabled="loading" />
      </label>

      <label>
        Page size
        <select :value="String(pageSize)" :disabled="loading" @change="updatePageSize($event.target.value)">
          <option v-for="size in pageSizeOptions" :key="size" :value="String(size)">{{ size }}</option>
        </select>
      </label>

      <div class="filter-actions">
        <button class="btn" type="submit" :disabled="loading">Apply</button>
        <button class="btn btn-secondary" type="button" :disabled="loading" @click="clearFilters">Clear</button>
      </div>
    </form>

    <p v-if="error" class="state-message error">{{ error || 'Failed to load audit logs' }}</p>
    <p v-if="degradedWarning" class="state-message warning">{{ degradedWarning }}</p>
    <p v-if="partialDataWarning" class="state-message warning">{{ partialDataWarning }}</p>
    <p v-if="staleData && !loading" class="state-message warning">Audit data may be stale. Refresh to verify operational status.</p>
    <p v-else-if="loading" class="state-message muted">{{ toAdminLoadingMessage('audit events') }}</p>
    <p v-else-if="!logs.length" class="state-message muted">No audit events found</p>

    <div v-else class="audit-table-wrap">
      <table class="audit-table">
        <thead>
          <tr>
            <th>Timestamp</th>
            <th>Actor</th>
            <th>Action</th>
            <th>Entity Type</th>
            <th>Entity Name</th>
            <th>Reason</th>
            <th>Outcome</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(log, index) in logs" :key="getLogKey(log, index)">
            <tr
              :class="['audit-summary-row', { 'is-expanded': isExpanded(log, index) }]"
              :data-testid="`audit-row-${getLogKey(log, index)}`"
              :aria-expanded="isExpanded(log, index) ? 'true' : 'false'"
              tabindex="0"
              @click="toggleExpandedLog(log, index)"
              @keydown.enter.prevent="toggleExpandedLog(log, index)"
              @keydown.space.prevent="toggleExpandedLog(log, index)"
            >
              <td>
                <span class="row-toggle-indicator" aria-hidden="true">{{ isExpanded(log, index) ? '▾' : '▸' }}</span>
                {{ formatAuditTimestamp(log.created_at || log.timestamp) }}
              </td>
              <td>{{ resolveActor(log) }}</td>
              <td>
                <span :class="['badge', `badge-${getAuditActionMeta(log.action).severity}`, `badge-group-${getAuditActionMeta(log.action).group}`]">
                  <span class="badge-icon" aria-hidden="true">{{ getAuditActionMeta(log.action).icon }}</span>
                  {{ getAuditActionMeta(log.action).label }}
                </span>
              </td>
              <td>{{ formatDetailValue(log.entity_type) }}</td>
              <td>{{ resolveEntityName(log) }}</td>
              <td>{{ formatDetailValue(log.reason, 'None') }}</td>
              <td>
                <span :class="['badge', `badge-${getAuditOutcomeMeta(log.success).className}`]">
                  <span class="badge-icon" aria-hidden="true">{{ getAuditOutcomeMeta(log.success).icon }}</span>
                  {{ getAuditOutcomeMeta(log.success).label }}
                </span>
              </td>
            </tr>

            <tr v-if="isExpanded(log, index)" class="audit-detail-row" :data-testid="`audit-detail-${getLogKey(log, index)}`">
              <td colspan="7">
                <section class="audit-detail-panel">
                  <header class="audit-detail-header">
                    <div class="audit-detail-heading">
                      <span :class="['badge', 'badge-detail', `badge-${getAuditActionMeta(log.action).severity}`, `badge-group-${getAuditActionMeta(log.action).group}`]">
                        <span class="badge-icon" aria-hidden="true">{{ getAuditActionMeta(log.action).icon }}</span>
                        {{ getAuditActionMeta(log.action).label }}
                      </span>
                      <span class="audit-detail-code">{{ formatDetailValue(log.action) }}</span>
                    </div>

                    <span :class="['badge', 'badge-detail', `badge-${getAuditOutcomeMeta(log.success).className}`]">
                      <span class="badge-icon" aria-hidden="true">{{ getAuditOutcomeMeta(log.success).icon }}</span>
                      {{ getAuditOutcomeMeta(log.success).label }}
                    </span>
                  </header>

                  <dl class="audit-detail-grid">
                    <div v-for="field in getAuditDetailFields(log)" :key="field.label" class="audit-detail-item">
                      <dt>{{ field.label }}</dt>
                      <dd>{{ field.value }}</dd>
                    </div>
                  </dl>

                  <section class="audit-metadata-section" aria-label="Audit metadata">
                    <h2>Metadata JSON</h2>

                    <p v-if="!getMetadataEntries(log).length" class="metadata-empty">None</p>

                    <ul v-else class="metadata-list">
                      <li v-for="entry in getMetadataEntries(log)" :key="entry.key" class="metadata-item">
                        <span class="metadata-label">{{ entry.label }}</span>
                        <pre v-if="entry.isCode" class="metadata-value metadata-code">{{ entry.value }}</pre>
                        <span v-else class="metadata-value">{{ entry.value }}</span>
                      </li>
                    </ul>
                  </section>
                </section>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <footer class="pagination-row">
      <button class="btn btn-secondary" type="button" :disabled="loading || !hasPreviousPage" @click="goToPreviousPage">
        Previous
      </button>
      <span>Page {{ page }} of {{ totalPages }} ({{ total }} events)</span>
      <button class="btn btn-secondary" type="button" :disabled="loading || !hasNextPage" @click="goToNextPage">
        Next
      </button>
    </footer>

    <section class="panel">
      <h2>Recent Exports</h2>
      <ul v-if="exportHistory.length" class="history-list">
        <li v-for="entry in exportHistory" :key="entry.id">
          {{ entry.export_scope }} - {{ entry.record_count }} rows - {{ formatAuditTimestamp(entry.exported_at) }}
          <span v-if="entry.partial_export_warning">(partial warning)</span>
        </li>
      </ul>
      <p v-else class="muted">No exports recorded yet.</p>
    </section>

    <AdminActionDialog
      :open="exportDialogOpen"
      title="Export Audit Logs"
      :message="`Export ${logs.length} visible event(s) as CSV. ${Number(total) > Number(logs.length) ? 'Current export may be partial.' : ''}`"
      :details="['Exports are tracked for operational history.', 'Use filters for scoped exportability at scale.']"
      tone="warning"
      confirm-label="Export CSV"
      :loading="exportLoading"
      loading-label="Preparing CSV..."
      @close="closeExportDialog"
      @confirm="exportAuditCsv"
    />

    <p v-if="exportError" class="state-message error">{{ exportError }}</p>
  </section>
</template>

<style scoped>
.audit-log-view {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  display: grid;
  gap: 1rem;
}

.audit-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.filter-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.75rem;
  align-items: end;
}

.filter-row label {
  display: grid;
  gap: 0.35rem;
  color: #111827;
  font-size: 0.9rem;
}

.filter-actions {
  display: flex;
  gap: 0.5rem;
}

input,
select {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  min-height: 40px;
  padding: 0.45rem 0.6rem;
  background: #fff;
}

.btn {
  border: 0;
  border-radius: 6px;
  background: #2563eb;
  color: #fff;
  min-height: 40px;
  padding: 0.45rem 0.75rem;
}

.btn-secondary {
  background: #4b5563;
}

.audit-table-wrap {
  overflow-x: auto;
}

.audit-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
}

.audit-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f9fafb;
}

.audit-table th,
.audit-table td {
  border-bottom: 1px solid #e5e7eb;
  text-align: left;
  padding: 0.6rem;
  font-size: 0.9rem;
  vertical-align: top;
}

.audit-summary-row {
  cursor: pointer;
}

.audit-summary-row:hover,
.audit-summary-row:focus-visible,
.audit-summary-row.is-expanded {
  background: #f8fafc;
}

.row-toggle-indicator {
  display: inline-block;
  width: 1rem;
  color: #6b7280;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  border-radius: 9999px;
  padding: 0.15rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
}

.badge-icon {
  line-height: 1;
}

.badge-danger,
.badge-failure {
  background: #fee2e2;
  color: #b91c1c;
}

.badge-warning {
  background: #fef3c7;
  color: #92400e;
}

.badge-group-auth {
  box-shadow: inset 0 0 0 1px #f59e0b;
}

.badge-group-lifecycle {
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.08);
}

.badge-success {
  background: #dcfce7;
  color: #166534;
}

.badge-neutral {
  background: #e5e7eb;
  color: #374151;
}

.badge-detail {
  align-self: flex-start;
}

.audit-detail-row td {
  background: #f8fafc;
  padding: 0;
}

.audit-detail-panel {
  display: grid;
  gap: 1rem;
  padding: 1rem;
}

.audit-detail-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
}

.audit-detail-heading {
  display: grid;
  gap: 0.4rem;
}

.audit-detail-code {
  color: #4b5563;
  font-size: 0.85rem;
  font-family: ui-monospace, SFMono-Regular, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, monospace;
  overflow-wrap: anywhere;
}

.audit-detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.75rem;
  margin: 0;
}

.audit-detail-item {
  min-width: 0;
  padding: 0.75rem;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
}

.audit-detail-item dt {
  margin: 0 0 0.35rem;
  color: #6b7280;
  font-size: 0.8rem;
  font-weight: 600;
}

.audit-detail-item dd {
  margin: 0;
  color: #111827;
  font-size: 0.9rem;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.audit-metadata-section {
  display: grid;
  gap: 0.75rem;
}

.audit-metadata-section h2 {
  margin: 0;
  font-size: 1rem;
}

.metadata-empty {
  margin: 0;
  color: #6b7280;
}

.metadata-list {
  list-style: none;
  display: grid;
  gap: 0.75rem;
  padding: 0;
  margin: 0;
}

.metadata-item {
  display: grid;
  gap: 0.35rem;
  padding: 0.75rem;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  min-width: 0;
}

.metadata-label {
  color: #6b7280;
  font-size: 0.8rem;
  font-weight: 600;
}

.metadata-value {
  color: #111827;
  font-size: 0.9rem;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.metadata-code {
  margin: 0;
  white-space: pre-wrap;
  font-family: ui-monospace, SFMono-Regular, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, monospace;
}

.state-message {
  border-radius: 6px;
  padding: 0.6rem;
}

.muted {
  color: #6b7280;
}

.error {
  color: #991b1b;
  background: #fee2e2;
}

.warning {
  color: #92400e;
  background: #fffbeb;
}

.panel {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.75rem;
  background: #fff;
}

.history-list {
  margin: 0;
  padding-left: 1rem;
  color: #374151;
}

.pagination-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
}

@media (max-width: 768px) {
  .audit-log-view {
    padding: 1rem;
  }

  .audit-header,
  .pagination-row,
  .filter-actions,
  .audit-detail-header {
    flex-direction: column;
    align-items: stretch;
  }

  .audit-detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>

